import { useMemo } from 'react';
import { cn } from '@/lib/utils';

export interface MemberAvatarProps {
  /** Stable identifier (user ULID) — used to derive a deterministic color. */
  id?: string | null;
  name?: string | null;
  email?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE: Record<NonNullable<MemberAvatarProps['size']>, string> = {
  xs: 'size-5 text-[10px]',
  sm: 'size-6 text-[11px]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
};

// Quietly saturated palette tuned to read well against `--background`.
// Order is intentional — most-distinct hues first so adjacent IDs don't
// collide visually in member rate tables and assignee stacks.
const PALETTE = [
  'bg-[oklch(0.78_0.10_30)] text-[oklch(0.25_0.05_30)]', // warm orange
  'bg-[oklch(0.78_0.10_140)] text-[oklch(0.25_0.05_140)]', // green
  'bg-[oklch(0.78_0.10_240)] text-[oklch(0.25_0.05_240)]', // blue
  'bg-[oklch(0.78_0.10_320)] text-[oklch(0.25_0.05_320)]', // pink
  'bg-[oklch(0.78_0.10_180)] text-[oklch(0.25_0.05_180)]', // teal
  'bg-[oklch(0.78_0.10_70)] text-[oklch(0.25_0.05_70)]', // yellow-green
  'bg-[oklch(0.78_0.10_280)] text-[oklch(0.25_0.05_280)]', // purple
  'bg-[oklch(0.78_0.10_15)] text-[oklch(0.25_0.05_15)]', // red-orange
];

const colorIndex = (key: string): number => {
  // FNV-1a-ish 32-bit folding hash. Stable across reloads, no deps.
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h % PALETTE.length;
};

const initialsFor = (name: string | null | undefined, email: string | null | undefined): string => {
  const source = (name ?? email ?? '').trim();
  if (source === '') return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const first = parts[0];
    if (!first) return '?';
    // Email fallback: take chars before "@".
    const local = first.includes('@') ? first.split('@')[0] : first;
    if (!local) return '?';
    return local.slice(0, 2).toUpperCase();
  }
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first || !last) return '?';
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
};

export const MemberAvatar = ({ id, name, email, size = 'sm', className }: MemberAvatarProps) => {
  const initials = useMemo(() => initialsFor(name, email), [name, email]);
  const bucket = useMemo(() => PALETTE[colorIndex(id ?? name ?? email ?? '')], [id, name, email]);
  const label = name ?? email ?? 'Unassigned';

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold tracking-tight uppercase select-none',
        SIZE[size],
        bucket,
        className,
      )}
      title={label}
      aria-label={label}
    >
      {initials}
    </span>
  );
};
